import { useEffect, useMemo, useState } from 'react';
import { mindtimeWazuhActiveResponseApi } from 'src/api/mindtime/wazuhActiveResponseApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useWazuhScope, type WazuhDateRangeOption } from 'src/mindtime/hooks/useWazuhScope';
import type { WazuhActiveResponseActionLabelOption } from 'src/types/mindtime/wazuhActiveResponse';
import logger from 'src/utils/logger';
import { buildActiveResponseActionLabelsQuery } from './automatedResponseDetailModel';
import { getActiveResponsePreviewActionLabels } from '../preview/automatedResponse';

type UseActiveResponseActionLabelOptionsArgs = {
  pageOrganizationId?: string;
  wazuhAgentId?: string;
  systemWazuhAgentId?: string;
  dateRange?: WazuhDateRangeOption;
  /** Override preview gate (endpoint tab uses useEdrDataGate('endpoints')). */
  isPreviewMode?: boolean;
  wait?: boolean;
};

/** GET /api/v1/active-responses/action-labels — Action Type filter options. */
export const useActiveResponseActionLabelOptions = ({
  pageOrganizationId,
  wazuhAgentId: wazuhAgentIdArg,
  systemWazuhAgentId,
  dateRange,
  isPreviewMode: previewModeOverride,
  wait: waitOverride,
}: UseActiveResponseActionLabelOptionsArgs = {}) => {
  const { scope, isReseller, dateRange: globalDateRange, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { isPreviewMode: gatePreviewMode, wait: gateWait } = useEdrDataGate('automated-response');
  const isPreviewMode = previewModeOverride ?? gatePreviewMode;
  const wait = waitOverride ?? gateWait;
  const profileOrgId = userData?.organization?.id;
  const wazuhAgentId = selectedEndpointId?.trim() || wazuhAgentIdArg;

  const [options, setOptions] = useState<WazuhActiveResponseActionLabelOption[]>([]);
  const [loading, setLoading] = useState(false);

  const queryParams = useMemo(
    () =>
      buildActiveResponseActionLabelsQuery({
        scope,
        profileOrgId,
        isReseller,
        dateRange: dateRange ?? globalDateRange,
        pageOrganizationId,
        wazuhAgentId,
        systemWazuhAgentId,
      }),
    [
      scope,
      profileOrgId,
      isReseller,
      dateRange,
      globalDateRange,
      pageOrganizationId,
      wazuhAgentId,
      systemWazuhAgentId,
    ]
  );

  useEffect(() => {
    if (wait) return undefined;

    if (isPreviewMode) {
      setOptions(getActiveResponsePreviewActionLabels(systemWazuhAgentId));
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    mindtimeWazuhActiveResponseApi
      .getActionLabels(queryParams)
      .then((labels) => {
        if (!cancelled) setOptions(labels);
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useActiveResponseActionLabelOptions');
          setOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [queryParams, isPreviewMode, wait, systemWazuhAgentId]);

  return { options, loading };
};
